import React, { useState, useRef } from "react";
import sparklesSmallTokens from "@amzn/meridian-tokens/base/icon/sparkles-small";
import { useLocation, useNavigate } from "react-router-dom";
import Column from "@amzn/meridian/column";
import Row from "@amzn/meridian/row";
import Text from "@amzn/meridian/text";
import { Button, Divider, Icon } from "@amzn/meridian";
import Link from "@amzn/meridian/link";
import arrowLeftTokens from "@amzn/meridian-tokens/base/icon/arrow-left-large";
import documentTokens from "@amzn/meridian-tokens/base/icon/document";
import { ClassificationResponse, ProductAttributes } from "src/types/classificationTypes";
import { useAppSelector } from "src/store/store";
import { extractSiteCode } from "src/constants/constants";
import MatchingContainersSheet from "./MatchingContainersSheet";
import AddToContainerSuccessPage from "./AddToContainerSuccessPage";
import PillButton from "../../UI/PillButton";
import "./classification.scss";


/** Small helper — renders one label/value row in the Product Attributes table */
const AttrRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <Column >
    <Row alignmentHorizontal="justify">
      <Text alignment="start">{label}</Text>
      <Text alignment="end">{value}</Text>
    </Row>
    <Divider />
  </Column>
);

const formatDimensions = (attrs: ProductAttributes): string => {
  if (!attrs?.dimensions) return "";
  const { width, length, height } = attrs.dimensions;
  const parts = [width?.value, length?.value, height?.value].filter(Boolean);
  if (parts.length === 0) return "";
  return `${parts.map((v) => v?.toFixed(2)).join(" x ")} ${height?.unit || "in"}`;
};

const formatWeight = (attrs: ProductAttributes): string => {
  if (!attrs?.weight) return "";
  return `${attrs.weight.value.toFixed(3)} ${attrs.weight.unit === "pounds" ? "lb" : attrs.weight.unit}`;
};

const formatHazmat = (attrs: ProductAttributes): string => {
  if (!attrs?.hazmat_statement?.length) return "";
  return attrs.hazmat_statement.map((h) => h.value.toUpperCase()).join(" / ");
};

// Minimum px(pixel) distance to register as a swipe (prevents accidental taps)
const SWIPE_THRESHOLD_PX = 30;

const ResultsPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const { siteLocation } = useAppSelector((state) => state.user);
  const siteCode = extractSiteCode(siteLocation);
  const data = (location.state as any)?.data as ClassificationResponse | undefined;

  const [activeImageIdx, setActiveImageIdx] = useState(0);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [addedContainer, setAddedContainer] = useState<string | null>(null);
  const touchStartX = useRef<number>(0);

  if (!data) {
    return (
      <Column width="100%" height="100vh" alignmentHorizontal="center" alignmentVertical="center" spacing="400">
        <Text>No classification data available.</Text>
        <PillButton text="Go Back" color="secondary" onClick={() => navigate("/home")} />
      </Column>
    );
  }

  // Destructure API response
  const {
    waste_profiles: wasteProfiles = [],
    donation_categories: donationCategories = [],
    sds_list: sdsList = [],
    product_attributes: productAttributes,
    waste_profile_source: wasteProfileSource,
    images = []
  } = data;

  const activeProfile = wasteProfiles[0];

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  // Image Swipe detection: positive diff = finger moved left = show next image,
  // negative diff = finger moved right = show previous image.
  const handleTouchEnd = (e: React.TouchEvent) => {
    const diff = touchStartX.current - e.changedTouches[0].clientX;

    if (diff > SWIPE_THRESHOLD_PX && activeImageIdx < images.length - 1) {
      setActiveImageIdx(activeImageIdx + 1);
    } else if (diff < -SWIPE_THRESHOLD_PX && activeImageIdx > 0) {
      setActiveImageIdx(activeImageIdx - 1);
    }
  };


  // Once the product is successfully added to a waste container, show the success page
  if (addedContainer) {
    return (
      <AddToContainerSuccessPage
        productName={data.name || "Unknown Product"} // Fallback to "Unknown Product" if the API returns no product name.
        asin={data.asin}
        containerName={addedContainer}
      />
    );
  }

  return (
    <Column className="app-gradient-bg pwa-scrollable" spacing="400" spacingInset="500 500 600">

      {/* Top bar: back arrow */}
      <Row alignmentVertical="center" spacingInset="400 none">
        <Button type="icon" onClick={() => navigate(-1)}>
          <Icon color="success" tokens={arrowLeftTokens} />
        </Button>

        <Column spacing="100" spacingInset="200">
          <Text type="h100">ASIN {data.asin}</Text>
          <Text type="b200" color="secondary">SITE  {siteCode} </Text>
        </Column>
      </Row>

      {/* Waste Profile ID: */}
      <Column spacing="300" spacingInset="200">
        <Text type="h100" color="secondary">
          WASTE PROFILE
        </Text>
        <Column className="home-card asin-waste-profile" spacingInset="400 none none">

          <Text type="h400" color="inverted" alignment="center" className="asin-waste-profile" >
            {activeProfile?.id ?? "—"}
          </Text>
          <Column className="home-card" spacingInset="400">
            <Row alignmentVertical="center" spacing="200">
              <Icon color="success" tokens={sparklesSmallTokens} />
              <Text type="h100" color="success" >
                AI INSIGHT
              </Text>
            </Row>
            <Column spacing="200">
              <Text color="secondary" type="h50">SDS Data Applied:</Text>

              {wasteProfileSource?.sds_attributes?.length ? (
                wasteProfileSource.sds_attributes.map((attr, idx) => (
                  <Text key={idx} type="b200">
                    {attr.name} {attr.value}. {attr.reason}
                  </Text>
                ))
              ) : (
                <Text tag="span">
                  No SDS data available.
                </Text>
              )}
            </Column>
          </Column>
        </Column>
      </Column>


      {/* Product name + ASIN */}
      <Column spacing="300" spacingInset="100 400">
        <Text type="h300">{data.name}</Text>
        <Text type="h100" color="secondary">ASIN: {data.asin}</Text>
      </Column>

      {/* Product images */}
      {images.length > 0 && (
        <Column alignmentHorizontal="center" spacing="400" spacingInset="100">
          <div onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
            <img
              style={{ width: "200px", height: "200px", objectFit: "contain" }}
              src={images[activeImageIdx]?.url}
              alt={data.name || "Product"}
            />
          </div>

          {/* Dot indicators */}
          {images.length > 1 && (
            <Row alignmentHorizontal="center" spacing="200">
              {images.map((_, idx) => (
                <div
                  key={idx}
                  className={`asin-dot ${idx === activeImageIdx ? "asin-dot--active" : ""}`}
                  onClick={() => setActiveImageIdx(idx)}
                />
              ))}
            </Row>
          )}
        </Column>
      )}

      {/* Donation Categories card */}
      <Column spacing="300" spacingInset="200">
        <Text type="h100" color="secondary">DONATION CATEGORIES</Text>
        {donationCategories.length > 0 && (
          <Column className="home-card" spacing="200" spacingInset="400 500">
            <Text tag="ul">
              {donationCategories.map((dc, idx) => (
                <Text key={idx} tag="li" color="secondary">{dc.name}</Text>
              ))}
            </Text>
          </Column>
        )}
      </Column>


      {/* Safety Data Sheets card — using Meridian Link + Icon + Text */}
      <Column spacing="300" spacingInset="200">
        <Text type="h100" color="secondary">SAFETY DATA SHEETS</Text>

        {sdsList.length > 0 && (
          <Column className="home-card" spacing="200" spacingInset="400">
            {sdsList.map((sds, idx) => (
              <Link
                key={idx}
                href={sds.url}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Row alignmentVertical="center" spacing="400">
                  <Icon color="primary" tokens={documentTokens} />
                  <Column spacing="none">
                    <Text>
                      SDS Document {sds.language ? `${sds.language.toUpperCase()}` : ""}
                    </Text>
                    <Text color="secondary" type="b200">
                      Tap to view
                    </Text>
                  </Column>
                </Row>
              </Link>
            ))}
          </Column>
        )}
      </Column>


      {/* Product Attributes card */}
      <Column spacingInset="200" spacing="300">
        <Text type="h100" color="secondary">PRODUCT ATTRIBUTES</Text>

        {productAttributes && (
          <Column className="home-card" spacingInset="400">
            {productAttributes.weight && <AttrRow label="Weight" value={formatWeight(productAttributes)} />}

            {productAttributes.dimensions && <AttrRow label="Dimensions" value={formatDimensions(productAttributes)} />}

            {productAttributes.hazmat_state && (
              <AttrRow label="Hazmat State" value={productAttributes.hazmat_state.charAt(0).toUpperCase() + productAttributes.hazmat_state.slice(1)} />
            )}

            {productAttributes.hazmat_statement && productAttributes.hazmat_statement.length > 0 && (
              <AttrRow label="Hazmat Statement" value={formatHazmat(productAttributes)} />
            )}

            {productAttributes.product_group && <AttrRow label="Product Group" value={productAttributes.product_group} />}
          </Column>
        )}
      </Column>

      {/* Add to Container and Override buttons */}
      <Column className="asin-sticky-footer" spacing="300">
        <PillButton text="ADD TO CONTAINER" color="success" onClick={() => setSheetOpen(true)} />
        <PillButton text="OVERRIDE" color="outline" />
      </Column>

      {/* Matching Containers bottom sheet */}
      {data && (
        <MatchingContainersSheet
          open={sheetOpen}
          asin={data.asin}
          wasteProfileId={activeProfile.id}
          onClose={() => setSheetOpen(false)}
          onSuccess={(containerName) => setAddedContainer(containerName)}
        />
      )}
    </Column>
  );
};

export default ResultsPage;
